// Tasks list: segmented Open / Done / All + DataTable + right-side Drawer.
// Behaviour unchanged — reuses /api/tasks and /api/tasks/{id}.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import {
  PageHeader, Panel, DataTable, Drawer, Chip, DsButton, AgentPortrait, DsTimeline,
} from "../ds";
import { DecisionDialog } from "../components/DecisionDialog";
import { RejectHoldDialog } from "../components/RejectHoldDialog";
import { CloseResolvedDialog } from "../components/CloseResolvedDialog";
import { Markdown } from "../components/Markdown";
import { useBilling } from "../context/BillingContext";
import { fmtUSD } from "../lib/format";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

const SEGMENTS = [
  { key: "open", label: "Open",
    states: ["draft","plan_requested","plan_ready","approved","building","emergent_testing","acceptance_testing","fix_requested","changes_requested","on_hold"] },
  { key: "done", label: "Done", states: ["done"] },
  { key: "all",  label: "All",  states: null },
];

export default function Board() {
  const [segment, setSegment] = useState("open");
  const [drawerTaskId, setDrawerTaskId] = useState(null);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState("approve");
  const [rhOpen, setRhOpen] = useState(false);
  const [rhAction, setRhAction] = useState("hold");
  const [closeOpen, setCloseOpen] = useState(false);
  const qc = useQueryClient();
  const { usdPerEcu } = useBilling();

  const { data: pdata } = useQuery({
    queryKey: ["projects-mini"],
    queryFn: async () => (await api.get("/projects")).data,
  });
  const { data } = useQuery({
    queryKey: ["tasks-all"],
    queryFn: async () => (await api.get("/tasks", { params: { limit: 500 } })).data,
    refetchInterval: 20000,
  });
  const { data: agentsReg } = useQuery({
    queryKey: ["agents-registry"],
    queryFn: async () => (await api.get("/settings/agents")).data,
  });
  const { data: taskDetail } = useQuery({
    queryKey: ["task", drawerTaskId],
    queryFn: async () => (await api.get(`/tasks/${drawerTaskId}`)).data,
    enabled: !!drawerTaskId,
    refetchInterval: 10000,
  });

  const projects = pdata?.projects || [];
  const projectMap = useMemo(
    () => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]
  );
  const agentByName = useMemo(
    () => Object.fromEntries((agentsReg?.agents || []).map((a) => [a.name, a])),
    [agentsReg]
  );

  const rows = useMemo(() => {
    const seg = SEGMENTS.find((s) => s.key === segment);
    return (data?.tasks || []).filter(
      (t) => !seg.states || seg.states.includes(t.state)
    );
  }, [data, segment]);

  const t = taskDetail?.task;
  const TERMINAL = ["done", "rejected"];
  const canDecide = t?.state === "plan_ready";
  const canResume = t?.state === "on_hold";
  const canHold = t && ["draft","plan_requested","plan_ready","approved","building","emergent_testing","acceptance_testing","fix_requested","changes_requested"].includes(t.state);
  const canReject = t && !TERMINAL.includes(t.state);
  const canClose = t && !TERMINAL.includes(t.state);

  const openRh = (action) => { setRhAction(action); setRhOpen(true); };
  const resumeTask = async () => {
    try {
      await api.post(`/tasks/${t.id}/resume`);
      toast.success("Task resumed.");
      qc.invalidateQueries({ queryKey: ["task", t.id] });
      qc.invalidateQueries({ queryKey: ["tasks-all"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onDrawerAction = () => {
    qc.invalidateQueries({ queryKey: ["task", drawerTaskId] });
    qc.invalidateQueries({ queryKey: ["tasks-all"] });
  };

  return (
    <div className="p-10 max-w-[1600px] mx-auto space-y-6">
      <PageHeader title="Tasks" subtitle="Every job the office runs, from draft to done" />

      <div className="flex gap-2" data-testid="tasks-segments">
        {SEGMENTS.map((s) => (
          <DsButton
            key={s.key}
            variant={segment === s.key ? "primary" : "default"}
            onClick={() => setSegment(s.key)}
            data-testid={`tasks-seg-${s.key}`}
          >
            {s.label}
          </DsButton>
        ))}
      </div>

      <Panel testId="tasks-panel">
        <DataTable
          testId="tasks-table"
          columns={[
            { key: "id", label: "ID", mono: true, width: 90,
              render: (r) => (
                <span
                  className="font-mono text-xs text-[color:var(--muted)] whitespace-nowrap tabular-nums"
                  title={r.id}
                >
                  T-{r.id.slice(-3)}
                </span>
              ) },
            { key: "title", label: "Task",
              render: (r) => (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="truncate whitespace-nowrap text-[color:var(--fg)]" title={r.title}>
                    {r.title}
                  </span>
                  {r.priority === "urgent" && <Chip value="urgent" />}
                </div>
              ) },
            { key: "project", label: "Project", width: 160,
              render: (r) => {
                const p = projectMap[r.project_id];
                if (!p) return <span className="text-[color:var(--muted)]">—</span>;
                return (
                  <Link
                    to={`/projects/${p.slug}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[color:var(--fg)] hover:text-[color:var(--cobalt)]"
                    data-testid={`task-project-link-${p.slug}`}
                  >
                    {p.name}
                  </Link>
                );
              } },
            { key: "owner", label: "Owner", width: 180,
              render: (r) => (
                <span className="inline-flex items-center gap-2">
                  <AgentPortrait agent={agentByName[r.assigned_agent] || { name: r.assigned_agent || "unassigned", department: "Product Studio" }} size={24} />
                  <span>{r.assigned_agent || "—"}</span>
                </span>
              ) },
            { key: "state", label: "State", width: 160,
              render: (r) => <Chip value={r.state} /> },
            { key: "emergent", label: "Emergent", right: true, width: 160,
              render: (r) => {
                const ecu = r.cost?.ecu ?? r.plan?.estimate_ecu;
                if (!ecu) return "—";
                const usd = Number(ecu) * (usdPerEcu || 0);
                return <span className="font-mono tabular-nums text-sm text-[color:var(--fg)]">{Number(ecu).toFixed(1)} <span className="text-[color:var(--muted)]">ECU</span> <span className="text-[color:var(--muted)]">(~${usd.toFixed(2)})</span></span>;
              } },
            { key: "claude", label: "Claude", right: true, width: 100,
              render: (r) => {
                const usd = r.cost?.usd ?? r.plan?.estimate_usd;
                return <span className="font-mono tabular-nums text-sm text-[color:var(--fg)]">{usd != null ? fmtUSD(usd) : "—"}</span>;
              } },
          ]}
          rows={rows}
          onRowClick={(r) => setDrawerTaskId(r.id)}
          empty="No tasks in this view."
        />
      </Panel>

      <Drawer open={!!drawerTaskId} onClose={() => setDrawerTaskId(null)} testId="task-drawer">
        {t && (
          <>
            <div className="font-mono text-xs text-[color:var(--muted)] mb-1 whitespace-nowrap tabular-nums">
              T-{t.id.slice(-3)} · {projectMap[t.project_id]?.name || "—"}
            </div>
            <h2 className="font-ui text-xl font-bold text-[color:var(--fg)]" data-testid="drawer-title">{t.title}</h2>
            <div className="mt-2"><Chip value={t.state} /></div>

            <dl className="mt-6 grid grid-cols-[90px_1fr] gap-y-2 text-sm">
              <dt className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em]">OWNER</dt>
              <dd className="text-[color:var(--fg)]">{t.assigned_agent || "—"}</dd>
              <dt className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em]">PRIORITY</dt>
              <dd className="text-[color:var(--fg)]">{t.priority}</dd>
              <dt className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em]">EMERGENT</dt>
              <dd className="font-mono tabular-nums text-[color:var(--fg)]">
                {t.plan?.estimate_ecu ? `${t.plan.estimate_ecu} ECU (~$${(t.plan.estimate_ecu * (usdPerEcu || 0)).toFixed(2)})` : "—"}
              </dd>
              <dt className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em]">CLAUDE</dt>
              <dd className="font-mono tabular-nums text-[color:var(--fg)]">{t.plan?.estimate_usd != null ? fmtUSD(t.plan.estimate_usd) : "—"}</dd>
            </dl>

            {t.plan?.summary && (
              <>
                <div className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em] mt-6">PLAN</div>
                <p className="text-sm text-[color:var(--fg)] mt-2">{t.plan.summary}</p>
              </>
            )}

            {canDecide && (
              <div className="mt-5 flex gap-2 flex-wrap">
                <DsButton variant="go"      onClick={() => { setDecision("approve"); setDecisionOpen(true); }} data-testid="drawer-approve">Approve</DsButton>
                <DsButton variant="default" onClick={() => { setDecision("changes"); setDecisionOpen(true); }} data-testid="drawer-changes">Request changes</DsButton>
                <DsButton variant="no"      onClick={() => { setDecision("reject");  setDecisionOpen(true); }} data-testid="drawer-reject">Reject</DsButton>
              </div>
            )}

            <div className="mt-5 flex gap-2 flex-wrap">
              {canResume && (
                <DsButton variant="go" onClick={resumeTask} data-testid="drawer-resume">
                  Resume
                </DsButton>
              )}
              {canHold && (
                <DsButton variant="default" onClick={() => openRh("hold")} data-testid="drawer-hold">
                  Hold
                </DsButton>
              )}
              {canReject && !canDecide && (
                <DsButton variant="no" onClick={() => openRh("reject")} data-testid="drawer-reject-any">
                  Reject
                </DsButton>
              )}
              {canClose && (
                <DsButton variant="go" onClick={() => setCloseOpen(true)} data-testid="drawer-close-resolved">
                  Close as resolved
                </DsButton>
              )}
              <Link
                to={`/tasks/${t.id}`}
                className="ml-auto inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-[color:var(--cobalt)] hover:brightness-125"
                data-testid="drawer-open-full"
              >
                Open full page <ExternalLink className="h-3 w-3" />
              </Link>
            </div>

            <div className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em] mt-8">TIMELINE</div>
            <div className="mt-3">
              <DsTimeline events={taskDetail?.events || []} />
            </div>

            {t.plan?.text && (
              <details className="mt-6">
                <summary className="cursor-pointer font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em]">FULL PLAN</summary>
                <div className="mt-3 text-sm"><Markdown>{t.plan.text}</Markdown></div>
              </details>
            )}
          </>
        )}
      </Drawer>

      <DecisionDialog
        open={decisionOpen}
        onOpenChange={setDecisionOpen}
        taskId={drawerTaskId}
        decision={decision}
        onDone={onDrawerAction}
      />
      <RejectHoldDialog
        open={rhOpen}
        onOpenChange={setRhOpen}
        taskId={drawerTaskId}
        action={rhAction}
        onDone={onDrawerAction}
      />
      <CloseResolvedDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        taskId={drawerTaskId}
        onDone={onDrawerAction}
      />
    </div>
  );
}
